/**
 * Permission-based action buttons
 * 
 * These components provide buttons and actions that are only visible/enabled
 * when the user has the required permissions.
 */

import React from 'react';
import { Button } from '@/components/ui/Button';
import { PermissionGuard } from './PermissionGuard';
import { usePermissions } from '@/hooks/usePermissions';
import { Permission } from '@/utils/permissions';

interface PermissionButtonProps extends React.ComponentProps<typeof Button> {
  /**
   * Single permission required to show/enable the button
   */
  permission?: Permission;
  /**
   * Multiple permissions - user must have ANY of these
   */
  anyOf?: Permission[];
  /**
   * Multiple permissions - user must have ALL of these
   */
  allOf?: Permission[];
  /**
   * Whether to hide the button when permission is denied (default: true)
   * If false, button will be disabled instead
   */
  hideWhenDenied?: boolean;
  /**
   * Custom permission check function
   */
  check?: (permissions: { can: (p: Permission) => boolean; canAny: (p: Permission[]) => boolean; canAll: (p: Permission[]) => boolean }) => boolean;
}

/**
 * Button component that respects user permissions
 */
export const PermissionButton: React.FC<PermissionButtonProps> = ({
  permission,
  anyOf,
  allOf,
  hideWhenDenied = true,
  check,
  disabled,
  children,
  ...buttonProps
}) => {
  const { can, canAny, canAll } = usePermissions();
  
  let hasAccess = false;
  
  if (check) {
    hasAccess = check({ can, canAny, canAll });
  } else if (permission) {
    hasAccess = can(permission);
  } else if (anyOf) {
    hasAccess = canAny(anyOf);
  } else if (allOf) {
    hasAccess = canAll(allOf);
  } else {
    // No permission specified, allow access
    hasAccess = true;
  }
  
  if (!hasAccess && hideWhenDenied) {
    return null;
  }
  
  return (
    <Button
      {...buttonProps}
      disabled={disabled || !hasAccess}
    >
      {children}
    </Button>
  );
};

/**
 * Link component that respects user permissions
 */
interface PermissionLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  permission?: Permission;
  anyOf?: Permission[];
  allOf?: Permission[];
  hideWhenDenied?: boolean;
  check?: (permissions: { can: (p: Permission) => boolean; canAny: (p: Permission[]) => boolean; canAll: (p: Permission[]) => boolean }) => boolean;
}

export const PermissionLink: React.FC<PermissionLinkProps> = ({
  permission,
  anyOf,
  allOf,
  hideWhenDenied = true,
  check,
  children,
  className = '',
  ...linkProps
}) => {
  const { can, canAny, canAll } = usePermissions();
  
  let hasAccess = false;
  
  if (check) {
    hasAccess = check({ can, canAny, canAll });
  } else if (permission) {
    hasAccess = can(permission);
  } else if (anyOf) {
    hasAccess = canAny(anyOf);
  } else if (allOf) {
    hasAccess = canAll(allOf);
  } else {
    hasAccess = true;
  }
  
  if (!hasAccess && hideWhenDenied) {
    return null;
  }
  
  const linkClassName = `${className} ${!hasAccess ? 'opacity-50 cursor-not-allowed' : ''}`.trim();
  
  return (
    <a
      {...linkProps}
      className={linkClassName}
      onClick={!hasAccess ? (e) => e.preventDefault() : linkProps.onClick}
    >
      {children}
    </a>
  );
};

/**
 * Generic action component that can be used for any interactive element
 */
interface PermissionActionProps {
  children: React.ReactNode;
  permission?: Permission;
  anyOf?: Permission[];
  allOf?: Permission[];
  hideWhenDenied?: boolean;
  disabledFallback?: React.ReactNode;
  check?: (permissions: { can: (p: Permission) => boolean; canAny: (p: Permission[]) => boolean; canAll: (p: Permission[]) => boolean }) => boolean;
}

export const PermissionAction: React.FC<PermissionActionProps> = ({
  children,
  permission,
  anyOf,
  allOf,
  hideWhenDenied = true,
  disabledFallback,
  check,
}) => {
  const { can, canAny, canAll } = usePermissions();
  
  let hasAccess = false;
  
  if (check) {
    hasAccess = check({ can, canAny, canAll });
  } else if (permission) {
    hasAccess = can(permission);
  } else if (anyOf) {
    hasAccess = canAny(anyOf);
  } else if (allOf) {
    hasAccess = canAll(allOf);
  } else {
    hasAccess = true;
  }
  
  if (!hasAccess) {
    if (hideWhenDenied) {
      return null;
    }
    return <>{disabledFallback}</>;
  }
  
  return <>{children}</>;
};